import { createContext, useContext, useEffect, type ReactNode } from "react";
import { useAppShell } from "./AppShellContext";
import { useAuth } from "./AuthContext";
import type { BrandConfig, BrandIdentifier } from "../data/appShellTypes";
import { detectSurface } from "../utils/surfaceDetection";

const BRAND_CONFIGS: Record<BrandIdentifier, BrandConfig> = {
  cortex: {
    identifier: "cortex",
    logoUrl: "/cortex-logo.png",
    primaryColor: "#1e293b",   // slate-800
    accentColor: "#4f46e5",    // indigo-600
    faviconUrl: "/favicon.png",
    displayName: "Cortex",
  },
  joveo: {
    identifier: "joveo",
    logoUrl: "/joveo-logo.png",
    primaryColor: "#1a1a2e",   // Joveo dark
    accentColor: "#6366f1",    // indigo-500
    faviconUrl: "/favicon.png",
    displayName: "Joveo",
  },
  neutral: {
    identifier: "neutral",
    logoUrl: "/cortex-logo.png",
    primaryColor: "#475569",   // slate-600
    accentColor: "#64748b",    // slate-500
    faviconUrl: "/favicon.png",
    displayName: "",
  },
};

interface BrandingContextValue {
  brand: BrandConfig;
  brandId: BrandIdentifier;
}

const BrandingContext = createContext<BrandingContextValue>({
  brand: BRAND_CONFIGS.cortex,
  brandId: "cortex",
});

export function BrandingProvider({ children }: { children: ReactNode }) {
  const { currentApp } = useAppShell();
  const { user } = useAuth();
  // External users always see Joveo branding, and so does everyone on the
  // partner-facing hosts (docs/developers/enable.joveo.ai) — signed out on
  // /login included. Only cortex.joveo.ai is Cortex.
  const surface = detectSurface();
  const joveoSurface = surface.brandIdentifier === "joveo";
  const brandId: BrandIdentifier = (user?.userType === "external" || joveoSurface) ? "joveo" : (currentApp?.brandIdentifier ?? "cortex");
  const brand = BRAND_CONFIGS[brandId];
  const title =
    surface.appSlug === "enable" ? "Joveo Enable"
      : surface.appSlug === "docs" ? "Joveo Docs"
        : brand.displayName;

  // Apply CSS custom properties + document title
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty("--brand-primary", brand.primaryColor);
    root.style.setProperty("--brand-accent", brand.accentColor);
    if (title) {
      document.title = title;
    }
  }, [brand, title]);

  return (
    <BrandingContext.Provider value={{ brand, brandId }}>
      {children}
    </BrandingContext.Provider>
  );
}

export function useBranding(): BrandingContextValue {
  return useContext(BrandingContext);
}
